// src/components/shared/ApplyFilterQuestionEditor.jsx
// Shared building blocks for editing a single Apply Filter question. Used by
// the Apply Filter editor (per-filter questions) and the Apply Filter Template
// management page (saved template questions). Both surfaces use the same
// question type metadata, config editors, and modal layout.

import React, { useEffect, useState } from 'react';
import { Modal } from './UI';
import { Plus } from 'lucide-react';

export const TYPE_META = [
  { type: 'TEXTBOX',              label: 'Text box',              hint: 'Single-line short answer' },
  { type: 'TEXTAREA',             label: 'Text area',             hint: 'Multi-line long answer' },
  { type: 'NUMBER',               label: 'Number entry',          hint: 'Numeric input' },
  { type: 'CHECKBOX',             label: 'Checkboxes',            hint: 'Applicant picks any number of options' },
  { type: 'RADIO',                label: 'Radio buttons',         hint: 'Applicant picks exactly one option' },
  { type: 'DROPDOWN',             label: 'Drop down',             hint: 'Applicant picks one from a list' },
  { type: 'DATE',                 label: 'Date',                  hint: 'Applicant picks a date' },
  { type: 'LINEAR_SCALE',         label: 'Linear scale',          hint: 'Applicant rates on a numeric scale' },
  { type: 'FILE_UPLOAD',          label: 'File upload',           hint: 'Applicant uploads a file (not scorable)' },
  { type: 'YES_NO_NEGOTIATE',     label: 'Yes / No / Negotiate',  hint: 'Three-option response' },
  { type: 'AVAILABILITY_DROPDOWN',label: 'Availability dropdown', hint: 'Applicant picks earliest start window' },
  { type: 'ADMIN_LEVEL_DROPDOWN', label: 'Admin level dropdown',  hint: 'Applicant picks current admin level' },
  { type: 'LANGUAGE_COMPETENCY',  label: 'Language competency',   hint: 'Per-skill proficiency (Reading / Writing / Listening / Speaking)' },
  { type: 'ASSOCIATE_EXPERIENCE', label: 'Associate experience',  hint: 'Applicant links matching items from their work history' },
  { type: 'ASSOCIATE_EDUCATION',  label: 'Associate education',   hint: 'Applicant links matching items from their qualifications' },
  { type: 'ASSOCIATE_ATTRIBUTES', label: 'Associate attributes',  hint: 'Applicant picks from their saved attributes' },
  { type: 'ASSOCIATE_SKILLS',     label: 'Associate skills',      hint: 'Applicant picks from their saved skills' },
];
export const TYPE_LABEL = Object.fromEntries(TYPE_META.map(t => [t.type, t.label]));

const YES_NO_NEGOTIATE = ['Yes', 'No', 'Negotiate'];
export const AVAILABILITY_OPTIONS = [
  { value: 'IMMEDIATELY',                label: 'Immediately' },
  { value: 'TWO_WEEKS_OR_LESS',          label: 'Within two weeks' },
  { value: 'TWO_WEEKS_TO_ONE_MONTH',     label: 'Two weeks to one month' },
  { value: 'MORE_THAN_ONE_MONTH',        label: 'More than one month' },
];

export const newId = () => crypto.randomUUID();

export function defaultConfig(type) {
  switch (type) {
    case 'CHECKBOX':
    case 'RADIO':
    case 'DROPDOWN':
      return { options: [] };
    case 'LINEAR_SCALE':
      return { min: 1, max: 5, minLabel: '', maxLabel: '' };
    case 'FILE_UPLOAD':
      return { acceptedMimeTypes: '.pdf,.doc,.docx' };
    case 'LANGUAGE_COMPETENCY':
      return { subFields: ['Reading', 'Writing', 'Listening', 'Speaking'] };
    default:
      return null;
  }
}

export const blankQuestion = (type) => ({
  id: newId(),
  order: 0,
  type,
  prompt: '',
  required: false,
  config: defaultConfig(type),
  correctAnswer: null,
});

export const AnswerPreview = ({ q }) => {
  const { type, correctAnswer, config } = q;
  if (correctAnswer === null || correctAnswer === undefined || correctAnswer === '') {
    return <span style={{ color: '#999', fontStyle: 'italic' }}>No correct answer set</span>;
  }
  if (type === 'CHECKBOX' && Array.isArray(correctAnswer)) return correctAnswer.join(', ');
  if (type === 'LINEAR_SCALE' && typeof correctAnswer === 'object') {
    return `${correctAnswer.min ?? ''}–${correctAnswer.max ?? ''} (of ${config?.min}–${config?.max})`;
  }
  if (type === 'AVAILABILITY_DROPDOWN') {
    return AVAILABILITY_OPTIONS.find(o => o.value === correctAnswer)?.label || String(correctAnswer);
  }
  return String(correctAnswer);
};

const OptionsEditor = ({ options, onChange, placeholderPrefix = 'Option' }) => {
  const update = (i, v) => { const next = [...options]; next[i] = v; onChange(next); };
  const remove = (i)    => onChange(options.filter((_, idx) => idx !== i));
  const add    = ()     => onChange([...options, '']);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {options.map((opt, i) => (
        <div key={i} style={{ display: 'flex', gap: 6 }}>
          <input className="form-input" value={opt} onChange={(e) => update(i, e.target.value)} placeholder={`${placeholderPrefix} ${i + 1}`} />
          <button type="button" className="btn btn-danger btn-sm" onClick={() => remove(i)}>×</button>
        </div>
      ))}
      <button type="button" className="btn btn-outline btn-sm" onClick={add}
        style={{ alignSelf: 'flex-start' }}>
        <Plus size={13} style={{ marginRight: 4 }} /> Add {placeholderPrefix.toLowerCase()}
      </button>
    </div>
  );
};

const CorrectAnswerEditor = ({ q, onChange }) => {
  const { type, config, correctAnswer } = q;
  const setCA = (val) => onChange(val);

  if (type === 'FILE_UPLOAD') {
    return <p style={{ fontSize: 12, color: '#777', margin: 0 }}>File uploads are not scored; applicants' uploads are collected for review only.</p>;
  }
  if (type === 'ADMIN_LEVEL_DROPDOWN' || type === 'LANGUAGE_COMPETENCY' ||
      type === 'ASSOCIATE_EXPERIENCE' || type === 'ASSOCIATE_EDUCATION' ||
      type === 'ASSOCIATE_ATTRIBUTES' || type === 'ASSOCIATE_SKILLS') {
    return <p style={{ fontSize: 12, color: '#777', margin: 0 }}>This question type doesn't take a single correct answer.</p>;
  }
  if (type === 'TEXTBOX' || type === 'TEXTAREA') {
    const Tag = type === 'TEXTAREA' ? 'textarea' : 'input';
    return (
      <Tag className="form-input" value={correctAnswer ?? ''}
        onChange={(e) => setCA(e.target.value || null)}
        rows={type === 'TEXTAREA' ? 3 : undefined}
        placeholder="Leave blank for no scoring" />
    );
  }
  if (type === 'NUMBER') {
    return (
      <input className="form-input" type="number" value={correctAnswer ?? ''}
        onChange={(e) => setCA(e.target.value === '' ? null : Number(e.target.value))}
        placeholder="Leave blank for no scoring" />
    );
  }
  if (type === 'DATE') {
    return (
      <input className="form-input" type="date" value={correctAnswer ?? ''}
        onChange={(e) => setCA(e.target.value || null)} />
    );
  }
  if (type === 'YES_NO_NEGOTIATE') {
    return (
      <select className="form-input" value={correctAnswer ?? ''}
        onChange={(e) => setCA(e.target.value || null)}>
        <option value="">— No correct answer —</option>
        {YES_NO_NEGOTIATE.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (type === 'AVAILABILITY_DROPDOWN') {
    return (
      <select className="form-input" value={correctAnswer ?? ''}
        onChange={(e) => setCA(e.target.value || null)}>
        <option value="">— No correct answer —</option>
        {AVAILABILITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (type === 'RADIO' || type === 'DROPDOWN') {
    const options = config?.options || [];
    return (
      <select className="form-input" value={correctAnswer ?? ''}
        onChange={(e) => setCA(e.target.value || null)}>
        <option value="">— No correct answer —</option>
        {options.map((o, i) => <option key={i} value={o}>{o || `Option ${i + 1}`}</option>)}
      </select>
    );
  }
  if (type === 'CHECKBOX') {
    const options = config?.options || [];
    const selected = Array.isArray(correctAnswer) ? correctAnswer : [];
    const toggle = (opt) => {
      const next = selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt];
      setCA(next.length ? next : null);
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <p style={{ fontSize: 12, color: '#666', margin: '0 0 4px 0' }}>Pick the option set that counts as correct.</p>
        {options.map((opt, i) => (
          <label key={i} style={{ fontSize: 13 }}>
            <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} /> {opt || `Option ${i + 1}`}
          </label>
        ))}
      </div>
    );
  }
  if (type === 'LINEAR_SCALE') {
    const ca = (correctAnswer && typeof correctAnswer === 'object') ? correctAnswer : { min: '', max: '' };
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#666' }}>Correct if answer is between</span>
        <input className="form-input" type="number" style={{ width: 70 }}
          value={ca.min ?? ''}
          onChange={(e) => setCA({ ...ca, min: e.target.value === '' ? null : Number(e.target.value) })} />
        <span>and</span>
        <input className="form-input" type="number" style={{ width: 70 }}
          value={ca.max ?? ''}
          onChange={(e) => setCA({ ...ca, max: e.target.value === '' ? null : Number(e.target.value) })} />
        <button type="button" className="btn btn-outline btn-sm" onClick={() => setCA(null)}>Clear</button>
      </div>
    );
  }
  return null;
};

const TypeConfigEditor = ({ q, onConfigChange }) => {
  const { type, config } = q;
  if (type === 'CHECKBOX' || type === 'RADIO' || type === 'DROPDOWN') {
    return (
      <div style={{ marginBottom: 14 }}>
        <label className="form-label">Options</label>
        <OptionsEditor
          options={config?.options || []}
          onChange={(options) => onConfigChange({ ...(config || {}), options })}
        />
      </div>
    );
  }
  if (type === 'LINEAR_SCALE') {
    const cfg = config || { min: 1, max: 5, minLabel: '', maxLabel: '' };
    return (
      <div style={{ marginBottom: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label className="form-label">Scale min</label>
          <input className="form-input" type="number" value={cfg.min ?? 1}
            onChange={(e) => onConfigChange({ ...cfg, min: Number(e.target.value) })} />
        </div>
        <div>
          <label className="form-label">Scale max</label>
          <input className="form-input" type="number" value={cfg.max ?? 5}
            onChange={(e) => onConfigChange({ ...cfg, max: Number(e.target.value) })} />
        </div>
        <div>
          <label className="form-label">Min label</label>
          <input className="form-input" value={cfg.minLabel ?? ''}
            onChange={(e) => onConfigChange({ ...cfg, minLabel: e.target.value })}
            placeholder="e.g. Poor" />
        </div>
        <div>
          <label className="form-label">Max label</label>
          <input className="form-input" value={cfg.maxLabel ?? ''}
            onChange={(e) => onConfigChange({ ...cfg, maxLabel: e.target.value })}
            placeholder="e.g. Excellent" />
        </div>
      </div>
    );
  }
  if (type === 'FILE_UPLOAD') {
    const cfg = config || { acceptedMimeTypes: '' };
    return (
      <div style={{ marginBottom: 14 }}>
        <label className="form-label">Accepted file types</label>
        <input className="form-input" value={cfg.acceptedMimeTypes ?? ''}
          onChange={(e) => onConfigChange({ ...cfg, acceptedMimeTypes: e.target.value })}
          placeholder=".pdf, .doc, .docx" />
      </div>
    );
  }
  if (type === 'LANGUAGE_COMPETENCY') {
    const subFields = (config?.subFields && config.subFields.length) ? config.subFields : ['Reading', 'Writing', 'Listening', 'Speaking'];
    return (
      <div style={{ marginBottom: 14 }}>
        <label className="form-label">Sub-fields (one proficiency rating each)</label>
        <OptionsEditor
          options={subFields}
          onChange={(next) => onConfigChange({ ...(config || {}), subFields: next })}
          placeholderPrefix="Sub-field"
        />
      </div>
    );
  }
  return null;
};

export const QuestionModal = ({ open, initial, onClose, onSave, title = 'Filter question', saveLabel = 'Save question' }) => {
  const [q, setQ] = useState(initial || blankQuestion('TEXTBOX'));
  useEffect(() => { if (open) setQ(initial || blankQuestion('TEXTBOX')); }, [open, initial]);
  if (!open) return null;

  const canSave = q.prompt.trim().length > 0;
  const update = (patch) => setQ(prev => ({ ...prev, ...patch }));
  const changeType = (newType) => {
    setQ(prev => ({
      ...prev,
      type: newType,
      config: defaultConfig(newType),
      correctAnswer: null,
    }));
  };

  return (
    <Modal open={open} onClose={onClose} title={title} width="620px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave} onClick={() => onSave(q)}>{saveLabel}</button>
        </div>
      }>
      <div style={{ marginBottom: 14 }}>
        <label className="form-label">Question type</label>
        <select className="form-input" value={q.type} onChange={(e) => changeType(e.target.value)}>
          {TYPE_META.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label className="form-label">Prompt</label>
        <textarea className="form-input" rows={2} value={q.prompt}
          onChange={(e) => update({ prompt: e.target.value })}
          placeholder="Write the question exactly as applicants will see it" />
      </div>

      <TypeConfigEditor q={q} onConfigChange={(config) => update({ config })} />

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13 }}>
          <input type="checkbox" checked={!!q.required} onChange={(e) => update({ required: e.target.checked })} />
          {' '}Mandatory — applicants cannot submit without answering.
        </label>
      </div>

      <div style={{ marginBottom: 4 }}>
        <label className="form-label">Correct answer (optional)</label>
        <CorrectAnswerEditor q={q} onChange={(correctAnswer) => update({ correctAnswer })} />
      </div>
    </Modal>
  );
};
